import AddIcon from "@mui/icons-material/Add";
import { Box, Button } from "@mui/material";
import { format, isThisISOWeek, isToday } from "date-fns";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  FilterSearchField,
  SearchFilter,
} from "../../common/components/functional/FilterSearchField/FilterSearchField";
import { GroupedTable } from "../../common/components/functional/GroupedTable/GroupedTable";
import { Heading1 } from "../../common/components/styled/styled";
import { ItemGroup } from "../invoices/invoiceAPI";
import { JobAdsFilters } from "./JobAdsFilters";
import { fetchJobAds, selectAllJobAds } from "./jobAdSlice";
import { JobAd, JobAdListItem, JobAdStatus } from "./types/common";

import LoadingSpinner from "../../common/components/functional/loadingSpinner/LoadingSpinner";
import { ReactComponent as DraftIcon } from "../../common/icons/attachmentDark.svg";
import { ReactComponent as PublishedIcon } from "../../common/icons/language.svg";
import { ReactComponent as FinishedIcon } from "../../common/icons/userChecked.svg";
import { AccessRightContext } from "../user/AccessRightProvider";
import { CityAndCounty } from "../../common/types/apiTypes";
import { leadingZerosDateFormat } from "../../common/helpers/formatDateTime";
import { JOB_AD_COLUMNS } from "./constants";
import {
  makeMultiSelectDisplay,
  mergeInitialAndStoredFilters,
} from "../../common/helpers/filters";
import { useTablePagination } from "../../hooks/useTablePagination";
import { TablePaginationProps } from "../../common/components/functional/pagination/types";
import { JOB_ADS_PAGE_SIZE } from "../../common/components/functional/pagination/constants";
import { selectExpertRecruitmentAreas } from "../../app/appSlice";
import isEmpty from "../../common/helpers/isEmpty";

const JobAdsPage = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.user);
  const jobAds: JobAd[] = useAppSelector((state) => selectAllJobAds(state));
  const { cursor, hits } = useAppSelector((state) => state.jobAds);
  const { t } = useTranslation(["jobAds"]);
  const { canWriteJobAd } = useContext(AccessRightContext);
  const { status } = useAppSelector((state) => state.jobAds);
  const isLoading = status === "pending";

  const expertAreaEntities = useAppSelector((state) =>
    selectExpertRecruitmentAreas(state),
  );
  const { expertAreas, areasDisplay, areasTooltip } = useMemo(() => {
    const areas = Object.entries(expertAreaEntities).map(([id, name]) => ({
      id,
      name,
    }));
    const { displayValue, tooltip } = makeMultiSelectDisplay(areas);
    return {
      expertAreas: areas,
      areasDisplay: displayValue,
      areasTooltip: tooltip,
    };
  }, [expertAreaEntities]);

  const defaultFilters: SearchFilter[] = [
    { name: "dateFrom", value: "", type: "date" },
    { name: "dateTo", value: "", type: "date" },
    {
      name: "area",
      value: expertAreas.map((area) => area.id),
      displayValue: areasDisplay,
      lookupValues: expertAreas,
      tooltip: areasTooltip,
    },
    {
      name: "contactPerson",
      value: user.user?.id || "",
      displayValue: t("common:me"),
      lookupValues: [{ id: user.user?.id || "", name: t("common:me") }],
    },
    { name: "locationCity", value: "" },
    { name: "customerCompanyName", value: "" },
    { name: "hasOrder", value: "" },
    { name: "campaign", value: "" },
    {
      name: "status",
      value: "PUBLISHED",
      lookupValues: [{ id: "PUBLISHED", name: t("PUBLISHED") }],
    },
  ];
  const JOB_AD_FILTERS_LOCAL_STORAGE = "jobAdFilters";
  const fromLocalStorage = localStorage.getItem(JOB_AD_FILTERS_LOCAL_STORAGE);
  const userFilters: SearchFilter[] =
    fromLocalStorage && JSON.parse(fromLocalStorage);
  const hasStoredAreaFilter =
    !!userFilters && userFilters.some((f) => f.name === "area");
  const combinedFilters: SearchFilter[] = mergeInitialAndStoredFilters(
    defaultFilters,
    userFilters || [],
  );

  const [filters, setFilters] = useState<SearchFilter[]>(combinedFilters);
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState<boolean>(false);
  const [searchString, setSearchString] = useState<string>("");
  const [jobAdGroups, setJobAdGroups] = useState<ItemGroup<JobAdListItem>[]>(
    [],
  );

  const getStatusIcon = (jobAdStatus: string) => {
    const DEFAULT_STYLES = {
      minWidth: 16,
      minHeight: 16,
      marginRight: "0.5em",
    };
    switch (jobAdStatus) {
      case JobAdStatus.DRAFT:
        return <DraftIcon style={{ ...DEFAULT_STYLES }} />;
      case JobAdStatus.PUBLISHED:
        return <PublishedIcon style={{ ...DEFAULT_STYLES }} />;
      case JobAdStatus.CLOSED:
      case JobAdStatus.EXPIRED:
        return <FinishedIcon style={{ ...DEFAULT_STYLES }} />;
      default:
        return;
    }
  };

  const formatJobAd = useCallback(
    (jobAd: JobAd, jobAdDateObject: Date) => {
      return {
        url: `/home/jobAds/${jobAd.id}`,
        id: jobAd.id || "",
        creationDate: format(jobAdDateObject, leadingZerosDateFormat),
        orderTitle: jobAd.orderTitle || "",
        title: jobAd.title || "",
        locationCity:
          jobAd.locations.map((i: CityAndCounty) => i.city).join(", ") || "",
        startDate: jobAd?.startDate
          ? format(new Date(jobAd.startDate), leadingZerosDateFormat)
          : "",
        endDate: format(new Date(jobAd.endDate), leadingZerosDateFormat),
        customer: jobAd.customerCompanyName || "",
        employer: jobAd.employer || "",
        publishingSites: !!jobAd.publishingSites
          ? jobAd.publishingSites.map((site) => site).join(", ")
          : "",
        contactPersonName: jobAd.contactPerson?.name ?? "",
        state: t(jobAd.status),
        totalApplicants: jobAd.totalApplicants
          ? jobAd.totalApplicants.toString()
          : "",
        flairComponent: getStatusIcon(jobAd.status),
        campaigns: jobAd.campaigns || [],
        newApplicants: jobAd.newApplicants?.toString() ?? "",
      };
    },
    [t],
  );

  // Group the items
  const constructJobAdGroups = useCallback(
    (jobAds: JobAd[]) => {
      const groups: ItemGroup<JobAdListItem>[] = [];

      jobAds.forEach((jobAd: JobAd) => {
        const creationDate = jobAd.creationDate;
        const creationDateObject = !!creationDate
          ? new Date(creationDate)
          : new Date();
        const creationDateYear = creationDateObject.getFullYear();

        let groupName: string;
        if (isToday(creationDateObject)) {
          groupName = "today";
        } else if (isThisISOWeek(creationDateObject)) {
          groupName = "thisWeek";
        } else {
          groupName = `${creationDateYear}-${String(creationDateObject.getMonth() + 1).padStart(2, "0")}`;
        }

        const formattedJobAdsData: JobAdListItem = formatJobAd(
          jobAd,
          creationDateObject,
        );
        const jobAdGroup = groups.find((group) => group.name === groupName);
        if (!jobAdGroup) {
          groups.push({
            name: groupName,
            year:
              creationDateYear < new Date().getFullYear()
                ? creationDateYear.toString()
                : undefined,
            items: [formattedJobAdsData],
          });
        } else {
          jobAdGroup.items.push(formattedJobAdsData);
        }
      });
      setJobAdGroups(groups);
    },
    [formatJobAd],
  );

  // -- -- -- -- -- FILTERS AND PAGINATION -- -- -- -- -- //

  const handleFilterMenuOpenClose = () => {
    setIsFilterMenuOpen(!isFilterMenuOpen);
  };
  const selectPrevTerm = (term: string) => {
    setSearchString(term);
  };

  const updateFilters = (filters: SearchFilter[]) => {
    setFilters(filters);
    localStorage.setItem(JOB_AD_FILTERS_LOCAL_STORAGE, JSON.stringify(filters));
  };

  const pageSize = parseInt(JOB_ADS_PAGE_SIZE, 10);

  const dispatchSearch = useCallback(
    (cursor?: string | null) => {
      const filterObject = filters.reduce((acc, filter) => {
        const { name, value } = filter;
        return { ...acc, [name]: value };
      }, {});
      dispatch(
        fetchJobAds({
          search: searchString,
          filters: filterObject,
          pageSize: pageSize.toString(),
          ...(cursor && { cursor }),
        }),
      );
    },
    [dispatch, filters, searchString, pageSize],
  );

  const { pageData, count, onPageChange, pageIndex, resetPagination } =
    useTablePagination<JobAd>({
      data: jobAds,
      cursor,
      hits,
      pageSize,
      fetchNext: (cursor) => dispatchSearch(cursor),
      preventDispatch: true,
    });

  const paginationProps: TablePaginationProps = {
    count,
    onPageChange,
    pageIndex,
    pageSize,
  };

  // -- -- -- -- -- useEffects -- -- -- -- -- //

  useEffect(() => {
    resetPagination();
    dispatchSearch();
  }, [dispatch, dispatchSearch, resetPagination]);

  useEffect(() => {
    constructJobAdGroups(pageData);
  }, [pageData, constructJobAdGroups]);

  useEffect(() => {
    if (isEmpty(expertAreas) || hasStoredAreaFilter) return;
    setFilters((prev) =>
      prev.map((f) =>
        f.name === "area"
          ? {
              ...f,
              value: expertAreas.map((a) => a.id),
              displayValue: areasDisplay,
              lookupValues: expertAreas,
              tooltip: areasTooltip,
            }
          : f,
      ),
    );
  }, [expertAreas, areasDisplay, areasTooltip, hasStoredAreaFilter]);

  // -- // -- // -- // -- // -- // -- // -- // -- // -- // -- // -- //

  return (
    <Box data-testid="job-ads-page-root">
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Heading1 variant="header">{t("jobAds")}</Heading1>
        <Box>
          {canWriteJobAd && (
            <Button
              variant="contained"
              sx={{ borderRadius: "18px", bgcolor: "primary.main" }}
              startIcon={<AddIcon />}
              onClick={() => navigate("/home/jobAds/new")}
            >
              {t("newJobAd")}
            </Button>
          )}
        </Box>
      </Box>
      <FilterSearchField
        isOpen={isFilterMenuOpen}
        toggleMenu={handleFilterMenuOpenClose}
        label={t("searchFromJobAds") || ""}
        value={searchString}
        onChange={(value) => setSearchString(value)}
        searchHistory={true}
        selectFromHistory={selectPrevTerm}
        translation={"jobAds"}
        filters={filters}
        updateFilters={updateFilters}
        isLoading={isLoading}
        localStorageName="searchFromJobAds"
        filtersLocalStorageName={JOB_AD_FILTERS_LOCAL_STORAGE}
        defaultFilters={defaultFilters}
      >
        <JobAdsFilters
          filters={filters}
          updateFilters={updateFilters}
          handleMenuToggle={handleFilterMenuOpenClose}
        />
      </FilterSearchField>
      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <>
          <GroupedTable
            columns={JOB_AD_COLUMNS}
            groups={jobAdGroups}
            itemCount={jobAds.length}
            pagination={paginationProps}
            translation="jobAds"
            hideAllCheckboxes
          />
        </>
      )}
    </Box>
  );
};

export default JobAdsPage;
